import { withZod } from '@remix-validated-form/with-zod'
import { useMemo } from 'react'
import { ValidatedForm, useFormContext } from 'remix-validated-form'
import { aUser, useAtomValue } from '~/atoms'
import {
  HeightField,
  HiddenFieldValues,
  HiddenFields,
  HtmlEditableField,
  NumberField,
  PropertyTypesField,
  SelectField,
  SubmitField,
  TextField,
} from '~/components'
import { useFormHelpers } from '~/hooks'
import {
  MemberRequestSchema,
  getDefaultValues,
  type MemberRequest,
  type MemberRequestType,
} from '~/models'

type MemberRequestProps = {
  formId: string
  action: string
  memberRequest?: MemberRequest
  requestType: MemberRequestType
}

export const memberRequestValidator = withZod(MemberRequestSchema)

export function AdminMemberRequestForm(props: MemberRequestProps) {
  const { formId, action, memberRequest, requestType } = props
  const formContext = useFormContext(formId)
  useFormHelpers(formId, formContext)
  const user = useAtomValue(aUser)

  const memberRequestDefaultValues = useMemo(() => {
    if (memberRequest) return memberRequest
    return getDefaultValues(user!, {
      propertyTypes: [],
      propertySubtypes: [],
      company: user!.company || undefined,
    })
  }, [memberRequest, user])

  return (
    <ValidatedForm
      id={formId}
      action={action}
      method="post"
      validator={memberRequestValidator}
      defaultValues={memberRequestDefaultValues}
      className="">
      <HiddenFields slugFields={['name']} />

      {user && <HiddenFieldValues name="company" value={user.company} />}

      <input type="hidden" name="requestType" value={requestType} />

      <div className="grid max-w-2xl grid-cols-12 gap-6">
        <TextField
          required
          className="col-span-12"
          label="Request Title"
          name="name"
        />
        <SelectField
          required
          className="col-span-6"
          label="Type"
          name="type"
          defaultNone={false}
          options={{ sale: 'Sale', lease: 'Lease', saleLease: 'Sale or Lease' }}
        />
        {memberRequest ? (
          <SelectField
            className="col-span-6"
            label="Status"
            name="status"
            defaultNone={false}
            options={
              requestType === 'ecDev'
                ? {
                    draft: 'Draft',
                    open: 'Open',
                    pending: 'Pending',
                    filled: 'Filled',
                  }
                : { open: 'Open', pending: 'Pending', filled: 'Filled' }
            }
          />
        ) : (
          <input type="hidden" name="status" value="open" />
        )}
        {requestType === 'ecDev' && (
          <>
            <TextField
              required
              className="col-span-6"
              label="Requesting Organization"
              name="developmentOrganization"
            />
            <TextField
              className="col-span-12 sm:col-span-6"
              label="Request Region"
              name="region"
            />
            <TextField
              required
              className="col-span-6"
              label="Contact First Name"
              name="contactFirstName"
            />
            <TextField
              required
              className="col-span-6"
              label="Contact Last Name"
              name="contactLastName"
            />
            <TextField
              className="col-span-6"
              label="Contact Number"
              name="contactNumber"
            />
            <TextField
              className="col-span-6"
              label="Contact Email"
              name="contactEmail"
            />
          </>
        )}
        <div className="col-span-12 flex flex-col gap-6">
          <PropertyTypesField />
        </div>
        <NumberField
          className="col-span-3 col-start-1"
          label="Min SF"
          name="meta.minSf"
          suffix="SF"
          maskType="decimal"
        />
        <NumberField
          className="col-span-3"
          label="Max SF"
          name="meta.maxSf"
          suffix="SF"
          maskType="decimal"
        />
        <NumberField
          className="col-span-3"
          label="Min Acres"
          name="meta.minAcres"
          suffix="ac"
          maskType="acres"
        />
        <NumberField
          className="col-span-3"
          label="Max Acres"
          name="meta.maxAcres"
          suffix="ac"
          maskType="acres"
        />
        <SelectField
          className="col-span-6"
          label="Occupancy Quarter"
          name="meta.occupancyQuarter"
          options={['Q1', 'Q2', 'Q3', 'Q4', 'Immediate']}
        />
        <NumberField
          className="col-span-6"
          label="Occupancy Year"
          name="meta.occupancyYear"
          maskType="year"
        />
        {requestType === 'ecDev' && (
          <>
            <NumberField
              className="col-span-6"
              label="Office SF"
              name="meta.office"
              suffix="SF"
              maskType="decimal"
            />
            <HeightField
              className="col-span-6"
              label="Minimum Ceiling Height"
              name="meta.ceilingHeightMin"
            />
            <HtmlEditableField
              label="Power"
              name="meta.powerDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="Water"
              name="meta.waterDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="Sewer"
              name="meta.sewerDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="Natural Gas"
              name="meta.naturalGasDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="HVAC"
              name="meta.hvacDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="Truck Loading"
              name="meta.loadingDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="Internet"
              name="meta.fiberOpticDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="Fire Protection"
              name="meta.fireProtectionDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="Lighting"
              name="meta.lightingDescription"
              className="col-span-12"
            />
            <HtmlEditableField
              label="Parking"
              name="meta.parkingDescription"
              className="col-span-12"
            />
          </>
        )}
        <HtmlEditableField
          label="Notes"
          name="meta.notes"
          className="col-span-12"
        />
      </div>

      <div className="">
        <SubmitField
          className="mt-6"
          formContext={formContext}
          isUpdating={true}
        />
      </div>
    </ValidatedForm>
  )
}
